import React, { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

import Loader from "../../components/Loader";
import useHubSettings from "../../../hooks/useHubSettings";

// The backend stores whatever it is given, so the check has to happen here.
const isAbsoluteHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * Customer portal address (CWA-91, US4).
 *
 * The address that goes into every configuration email, so the team can point
 * customers at the test or the production portal without a deployment.
 */
const PortalSettingsPage: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { hubBaseUrl, loading, error, save } = useHubSettings(toast);
  const [value, setValue] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(hubBaseUrl);
  }, [hubBaseUrl]);

  const submit = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setValidationError("Please enter the portal address.");
      return;
    }
    if (!isAbsoluteHttpUrl(trimmed)) {
      setValidationError(
        "Enter a full web address starting with http:// or https://."
      );
      return;
    }
    setValidationError(null);
    setSaving(true);
    try {
      await save(trimmed);
    } finally {
      setSaving(false);
    }
  };

  const dirty = value.trim() !== hubBaseUrl;

  return (
    <div className="card onboarding-page">
      <Toast ref={toast} />

      <h2 className="mt-0 mb-2">Portal Settings</h2>
      <p className="text-500 mt-0 mb-3">
        The address customers are sent to in their configuration email.
      </p>

      {loading && <Loader />}

      {!loading && error && (
        <Message
          severity="error"
          className="w-full mb-3"
          text="The portal address could not be loaded."
        />
      )}

      {!loading && !error && (
        <>
          {validationError && (
            <Message
              severity="error"
              className="w-full mb-3"
              text={validationError}
            />
          )}

          <div className="field p-fluid mb-3" style={{ maxWidth: "34rem" }}>
            <label htmlFor="portal-address" className="block mb-1">
              Customer portal address
            </label>
            <InputText
              id="portal-address"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setValidationError(null);
              }}
              placeholder="https://hub.cocharge.io"
              aria-label="Customer portal address"
            />
            <small className="text-500">
              Customers open this to see their station setup instructions.
            </small>
          </div>

          <Button
            label="Save"
            icon="pi pi-check"
            onClick={submit}
            loading={saving}
            disabled={saving || !dirty}
          />
        </>
      )}
    </div>
  );
};

export default PortalSettingsPage;
